import React, { useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import { GitCompareArrows } from 'lucide-react';
import * as z from 'zod';

import { type TypeModel } from '../../common/dataTypes';
import { useGetActualData } from '../../hooks/tanstack/useActualDataHook';
import { useGetModels } from '../../hooks/tanstack/useModelsHook';
import {
  useGetModelComparison,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import useModelComparisonStore from '../../hooks/zustand/useModelComparisonStore';
import cn from '../../utils/cn';
import { arrangeByBase } from '../../utils/helpers';
import FormAutoComplete from '../form/FormAutoComplete';
import Spinner from '../Spinner';

import ModelComparisonOutputTabs from './ModelComparisonOutputTabs';

const ModelComparisonProps = z.object({
  modelA: z.string().min(1, {
    message: "Left side of the comparison can't be empty",
  }),
  modelB: z.string().min(1, {
    message: 'Right side of the comparison can not be empty',
  }),
});

type ModelComparisonType = z.infer<typeof ModelComparisonProps>;

const ModelComparisonView = () => {
  const { projectId, orgId } = useGetRouteParams();

  const form = useForm<ModelComparisonType>({
    resolver: zodResolver(ModelComparisonProps),
  });

  const { watch, handleSubmit, formState } = form;

  const { modelA, modelB } = watch();

  const { errors } = formState;

  const { data: models, isLoading: isLoadingModels } = useGetModels(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { data: actualData, isLoading: isLoadingActualData } = useGetActualData(
    {
      orgId,
      projectId,
    }
  );

  const {
    isLoading: isLoadingModelComparison,
    run: runModelComparison,
    refetch: refetchModelComparison,
    data: modelComparisonData,
    isRunningServer: isRunningModelComparisonServer,
  } = useGetModelComparison({
    modelA,
    modelB,
  });

  const updateIsRunningModelComparison = useModelComparisonStore(
    (state) => state.updateIsRunningModelComparisonOutput
  );

  const onSubmit: SubmitHandler<ModelComparisonType> = async (data) => {
    if (!data.modelA || !data.modelB) {
      return;
    }

    updateIsRunningModelComparison(true);

    runModelComparison({
      orgId,
      projectId,
      data: {
        modelA: data.modelA.toLowerCase(),
        modelB: data.modelB.toLowerCase(),
      },
    });
  };

  const listModel = useMemo(
    () =>
      models
        ? arrangeByBase(models.filter((m) => !m.sharing) as TypeModel[])
            .map((model) => ({
              label: model.name,
              value: model.id.toString(),
            }))
            .concat(
              actualData?.length
                ? [
                    {
                      label: 'Actual Data',
                      value: 'actual',
                    },
                  ]
                : []
            )
        : [
            {
              label: '',
              value: '',
            },
          ],
    [models, actualData]
  );

  const isLoading =
    isRunningModelComparisonServer ||
    isLoadingModelComparison ||
    isLoadingModels ||
    isLoadingActualData;

  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-bold text-2xl">Compare models</h1>
      <div className="flex flex-col gap-3 rounded-[4px] border border-[#E4E7EC] bg-[#FFF] px-6 py-4">
        <h2 className="font-bold">Instruction</h2>
        <p className="font-normal">
          The comparison result is calculated by subtracting Model A values from
          Model B values (B - A). In the results, green indicates an increase,
          while red indicates a decrease.
        </p>
      </div>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn('flex items-end justify-between', {
            'items-center': errors.modelA || errors.modelB,
          })}
        >
          <div className="flex w-[55%] gap-4 items-start">
            <FormAutoComplete
              label="Model A"
              name="modelA"
              disabled={isLoading}
              options={listModel.filter((m) => m.value !== modelB)}
            />
            <FormAutoComplete
              label="Model B"
              name="modelB"
              disabled={isLoading}
              options={listModel.filter((m) => m.value !== modelA)}
            />
          </div>
          <Button
            disabled={isLoading}
            style={{
              color: 'white',
              display: 'flex',
              gap: 4,
              backgroundColor: '#B8341B',
              opacity: isLoading ? 0.5 : 1,
            }}
            type="submit"
          >
            <GitCompareArrows size={20} />
            <p>Run Comparison</p>
            {isRunningModelComparisonServer && <Spinner className="h-4 w-4" />}
          </Button>
        </form>
      </FormProvider>
      <div>
        <ModelComparisonOutputTabs baseId={modelA} targetId={modelB} />
      </div>
    </div>
  );
};

export default ModelComparisonView;
